/**
 * React Router configuration
 * Defines all application routes
 */

import {
  createBrowserRouter,
  Navigate,
  useLocation,
  type RouteObject,
} from "react-router-dom";
import { isLocatorHost, LOCATOR_PATH } from "./utils/locatorHost";
import { RequireAuth, RequireRole, useAuth } from "./contexts/AuthContext";
import { getDefaultRouteForRole } from "./utils/roleRouting";

// Layout components
import DashboardLayout from "./components/layout/DashboardLayout";

// Pages
import Login from "./pages/Login";
import AdhocReward from "./pages/AdhocReward";
import OAuthConsent from "./pages/OAuthConsent";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import SetupAccount from "./pages/SetupAccount";
import ConfirmReset from "./pages/ConfirmReset";
import VerifyInvitation from "./pages/VerifyInvitation";
import Dashboard from "./pages/Dashboard";
import PartnerDashboard from "./pages/PartnerDashboard";
import Organizations from "./pages/Organizations";
import OrganizationDetail from "./pages/OrganizationDetail";
import OrganizationEdit from "./pages/OrganizationEdit";
import LocationAdd from "./pages/LocationAdd";
import LocationEdit from "./pages/LocationEdit";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import ScreenPop from "./pages/ScreenPop";
import Segments from "./pages/Segments";
import SuppressionList from "./pages/SuppressionList";
import Deduplication from "./pages/Deduplication";
import Tasks from "./pages/Tasks";
import SupportTasks from "./pages/SupportTasks";
import PendingInvitations from "./pages/PendingInvitations";
import Events from "./pages/Events";
import EventsCalendar from "./pages/EventsCalendar";

/**
 * The calendar is the default Events view (owner decision 2026-09-03).
 *
 * A blunt redirect would break every filtered link, bookmark and export URL — including
 * the ones the calendar itself generates, which all carry ?type=&from=&to=. So the
 * redirect fires ONLY for a bare /events with no query string at all. Anything carrying
 * parameters is someone asking for a specific list, and still gets the list.
 *
 * The calendar's own "List view" button therefore links to /events?view=list: it needs a
 * parameter, or it would bounce straight back here and the two views would loop.
 */
const EventsIndex: React.FC = () => {
  const { search } = useLocation();
  if (!search || search === "?") {
    return <Navigate to="/events/calendar" replace />;
  }
  return <Events />;
};
import EventPerformance from "./pages/EventPerformance";
import EventDetail from "./pages/EventDetail";
import EventCreate from "./pages/EventCreate";
import EventImport from "./pages/EventImport";
import EventsMissingLead from "./pages/EventsMissingLead";
import EventEdit from "./pages/EventEdit";
import Interactions from "./pages/Interactions";
import Settings from "./pages/Settings";
import WhatsNew from "./pages/WhatsNew";
import SmsReviewQueue from "./pages/SmsReviewQueue";
import SmsInbox from "./pages/SmsInbox";
import SmsThread from "./pages/SmsThread";
import SmsConversationsGate from "./components/sms/SmsConversationsGate";
import { SMS_CONVERSATION_ROLES } from "./hooks/useSmsConversationsAvailable";
import ErrorLogs from "./pages/ErrorLogs";
import VoucherRedemption from "./pages/VoucherRedemption";
/**
 * Legacy /checkin and /eventcheckin -> /self-checkin, carrying ?code= through.
 * A bare <Navigate> would drop the query string, which is the only thing those URLs carry.
 */
const LegacyCheckinRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/self-checkin${search}`} replace />;
};
import EventRegister from "./pages/EventRegister";
import Redemptions from "./pages/Redemptions";
import VoucherTemplates from "./pages/VoucherTemplates";
import CampaignDetail from "./pages/CampaignDetail";
import Campaigns from "./pages/Campaigns";
import CampaignCreate from "./pages/CampaignCreate";
import CampaignLookups from "./pages/CampaignLookups";
import CampaignLookupEditor from "./pages/CampaignLookupEditor";
import Content from "./pages/Content";
import ContentViewer from "./pages/ContentViewer";
import PartnerOffers from "./pages/PartnerOffers";
import PartnerOfferDetail from "./pages/PartnerOfferDetail";
import FlightDetail from "./pages/FlightDetail";
import Sequences from "./pages/Sequences";
import SequenceCreate from "./pages/SequenceCreate";
import SequenceDetail from "./pages/SequenceDetail";
import SequenceErrorTriage from "./pages/SequenceErrorTriage";
import Partners from "./pages/Partners";
import PartnerDetail from "./pages/PartnerDetail";
import Offers from "./pages/Offers";
import CalendarRequests from "./pages/CalendarRequests";
import EventLandingPage from "./pages/EventLandingPage";
import GuestConfirmPage from "./pages/GuestConfirmPage";
import UniversalSelfCheckin from "./pages/UniversalSelfCheckin";
import TokenRedeem from "./pages/TokenRedeem";
import VoucherLocator from "./pages/VoucherLocator";
import ForwardToFriend from "./pages/ForwardToFriend";
import QRCodeChecker from "./pages/QRCodeChecker";
import SalesTeam from "./pages/SalesTeam";
import SalesPositionDetail from "./pages/SalesPositionDetail";
import AccountReviews from "./pages/AccountReviews";
import AccountReviewDetail from "./pages/AccountReviewDetail";
import SalesCalendar from "./pages/SalesCalendar";
import ScheduleEventConfirm from "./pages/ScheduleEventConfirm";
import RequireRoleOrSchedulingDelegate from "./components/auth/RequireRoleOrSchedulingDelegate";
import LandingPages from "./pages/LandingPages";
import LandingPageDetail from "./pages/LandingPageDetail";
import LandingPagePublic from "./pages/LandingPagePublic";
import FormHandlers from "./pages/FormHandlers";
import NeverBounceCampaignAdmin from "./pages/NeverBounceCampaignAdmin";
import FormHandlerEdit from "./pages/FormHandlerEdit";
import FormHandlerSubmissions from "./pages/FormHandlerSubmissions";
import ContactAttributesAdmin from "./pages/ContactAttributesAdmin";
import EmailSubscriptionGroups from "./pages/EmailSubscriptionGroups";
import AdSyncRuns from "./pages/AdSyncRuns";
import AdSyncRunDetail from "./pages/AdSyncRunDetail";
import RepairGenericCTAs from "./pages/admin/RepairGenericCTAs";
import ZuoraReconciliationQueue from "./pages/admin/ZuoraReconciliationQueue";
import ZuoraHeldWelcomes from "./pages/admin/ZuoraHeldWelcomes";
import SmsRetention from "./pages/admin/SmsRetention";
import PromotionRules from "./pages/admin/PromotionRules";
import TicketRewards from "./pages/admin/TicketRewards";
import AttorneyCoverage from "./pages/admin/AttorneyCoverage";
import AttorneyQuestions from "./pages/AttorneyQuestions";
import MsrSchedule from "./pages/msr/MsrSchedule";
import MsrEventDetail from "./pages/msr/MsrEventDetail";
import MsrAvailability from "./pages/msr/MsrAvailability";
import MsrSalesHistory from "./pages/msr/MsrSalesHistory";
import MsrVerifySale from "./pages/msr/MsrVerifySale";
import MsrTeam from "./pages/MsrTeam";
import MemberLookup from "./pages/msr/MemberLookup";
import BoothCheckin from "./pages/BoothCheckin";
import Reports from "./pages/Reports";
import EventsByOrganizationReport from "./pages/reports/EventsByOrganization";
import EventAttendanceReport from "./pages/reports/EventAttendance";
import ConversionSummaryReport from "./pages/reports/ConversionSummary";
import PromoterFeeReconciliationReport from "./pages/reports/PromoterFeeReconciliation";
import MsrBookOfBusinessReport from "./pages/reports/MsrBookOfBusiness";
import VerifiedMembersReport from "./pages/reports/VerifiedMembers";
import SalesActivityByTypeReport from "./pages/reports/SalesActivityByType";
import SalesByRepReport from "./pages/reports/SalesByRep";
import PhoneSalesReport from "./pages/reports/PhoneSales";
import CounterSalesReport from "./pages/reports/CounterSales";
import CounterSalesAggregateReport from "./pages/reports/CounterSalesAggregate";
import SmsRetentionHeldReport from "./pages/reports/SmsRetentionHeld";
import PhoneSalesList from "./pages/sales-activities/PhoneSalesList";
import PhoneSaleNew from "./pages/sales-activities/PhoneSaleNew";
import PhoneSaleDetail from "./pages/sales-activities/PhoneSaleDetail";
import CommissionEstimator from "./pages/CommissionEstimator";
import CommissionEstimatorPublic from "./pages/CommissionEstimatorPublic";
import EmailPreferences from "./pages/EmailPreferences";
import { isFeatureEnabled } from "./utils/featureFlags";
import { FINANCE_ROLES } from "./hooks/useFinanceAccess";
import FinanceLayout from "./pages/finance/FinanceLayout";
import FinanceExpenses from "./pages/finance/FinanceExpenses";
import FinanceApprovals from "./pages/finance/FinanceApprovals";
import FinanceAP from "./pages/finance/FinanceAP";
import FinanceSettings from "./pages/finance/FinanceSettings";

/**
 * Read the role from the cached profile in localStorage, if present and valid.
 * Used only as a fast-path fallback before AuthContext finishes loading — the
 * live profile from useAuth() is the authority.
 */
function getCachedProfileRole(): string | null {
  try {
    const cached = localStorage.getItem("usls_user_profile");
    if (!cached) {
      return null;
    }
    const { profile } = JSON.parse(cached);
    const role = profile?.role;
    return typeof role === "string" ? role : null;
  } catch {
    return null;
  }
}

/**
 * Redirect root "/" to the appropriate landing page based on the AUTHENTICATED
 * user's role.
 *
 * Auth-aware (bug fix): previously this rendered before the auth check using
 * only localStorage, and defaulted an unauthenticated (or cache-miss) visitor to
 * /dashboard. That /dashboard destination was then stored as `returnTo` by
 * RequireAuth and later forced a support agent onto Access Denied. Now:
 *   - While auth is still loading → show a spinner (don't guess).
 *   - Authenticated → send to the role's default via the shared mapping.
 *   - Not authenticated → go straight to /login (no /dashboard detour, so no
 *     bogus returnTo is ever stored for "/").
 * A cached role is used only as a pre-load fast path.
 */
function RoleBasedRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const role = user?.role ?? getCachedProfileRole();

  if (role) {
    return <Navigate to={getDefaultRouteForRole(role)} replace />;
  }

  // Unauthenticated: go to /login directly rather than bouncing through
  // /dashboard (which would store an unusable returnTo).
  return <Navigate to="/login" replace />;
}

/**
 * Application routes configuration
 */
const crmRoutes: RouteObject[] = [
  {
    path: "/",
    element: <RoleBasedRedirect />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/oauth/consent",
    element: <OAuthConsent />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    // Forced password change after an admin set a temporary password. Behind
    // RequireAuth so the AuthContext force-change gate allows ONLY this route
    // (the gate redirects all other authenticated routes here while the flag is set).
    path: "/force-password-change",
    element: (
      <RequireAuth>
        <ForcePasswordChange />
      </RequireAuth>
    ),
  },
  {
    path: "/setup-account",
    element: <SetupAccount />,
  },
  {
    path: "/confirm-reset",
    element: <ConfirmReset />,
  },
  {
    path: "/verify-invitation",
    element: <VerifyInvitation />,
  },
  {
    path: "/redeem",
    element: <VoucherRedemption />,
  },
  {
    // PUBLIC Voucher Redemption Locator — no auth. Linked from the voucher email as
    // /voucher-locations?code={{voucher_code}}; results come from the voucher-locator edge function.
    path: "/voucher-locations",
    element: <VoucherLocator />,
  },
  {
    path: "/email-preferences/unsubscribe",
    element: <EmailPreferences />,
  },
  {
    path: "/email-preferences/manage",
    element: <EmailPreferences />,
  },
  {
    // PUBLIC standalone Commission Calculator — no auth. A sales leader can demo it in a presentation and
    // share it en masse without granting CRM access. Rates via the bounded anon RPC
    // get_public_commission_estimator_rates (single source of truth; anon never touches the table).
    path: "/commission-calculator-public",
    element: <CommissionEstimatorPublic />,
  },
  {
    // Legacy URL redirect — the page was renamed Estimator → Calculator. Keep old shared/QR links working.
    path: "/commission-estimator-public",
    element: <Navigate to="/commission-calculator-public" replace />,
  },
  {
    // Conversion Engine booth check-in — STANDALONE full-bleed mobile route (NOT
    // inside DashboardLayout, so no sidebar crushes it on a phone). UNLIKE /redeem
    // this is NOT public: it stays behind RequireAuth + RequireRole because its
    // RPCs need an authenticated booth user (auth.uid() + role) — making it public
    // would reintroduce the /redeem anon-RLS problems. Roles mirror the
    // process_voucher_checkin server-side allow-list.
    path: "/booth-checkin",
    element: (
      <RequireAuth>
        <RequireRole
          allowedRoles={[
            "super_admin",
            "superadmin",
            "admin",
            "manager",
            "regional_manager",
            "territory_manager",
            "staff",
            "msr",
          ]}
        >
          <BoothCheckin />
        </RequireRole>
      </RequireAuth>
    ),
  },
  {
    // Event Field Check-in RETIRED 2026-08 — the standalone mobile walk-in page was
    // redundant and confusing (an event once routed attendees here by mistake). Operators
    // now do assisted walk-in check-in on the event page's Operations tab (EventWalkinPanel,
    // which emails the primary's ticket correctly), and public/self check-in is /self-checkin.
    // The route redirects to /events so old bookmarks/QRs land somewhere sensible; /events
    // enforces its own auth. The EventFieldCheckin page component was removed.
    path: "/event-field-checkin",
    element: <Navigate to="/events" replace />,
  },
  // RETIRED 2026-09-23. Both routes rendered EventCheckin, whose lookup RPC
  // (get_attendee_by_checkin_code) referenced columns removed by a schema rename and so had NEVER
  // worked -- every QR code the event page produced pointed here and threw. The supported surfaces
  // are the event's Operations tab and /self-checkin, and /self-checkin?code= already resolves a
  // CI- admission code. The redirect PRESERVES the query string so QR codes already printed or
  // distributed start working instead of breaking.
  {
    path: "/checkin",
    element: <LegacyCheckinRedirect />,
  },
  {
    path: "/eventcheckin",
    element: <LegacyCheckinRedirect />,
  },
  {
    path: "/event-register",
    element: <EventRegister />,
  },
  {
    path: "/view/:id",
    element: <ContentViewer />,
  },
  {
    path: "/register/:id",
    element: <EventLandingPage />,
  },
  {
    // PUBLIC guest confirmation page (invite-a-guest Slice 3). An invited guest lands here from their
    // email link; recognized members auto-confirm, non-members add their ZIP and confirm.
    path: "/guest-confirm/:landingPageId",
    element: <GuestConfirmPage />,
  },
  {
    // RETIRED (2026-09-09). The per-event public self-check-in page was superseded on
    // 2026-08-12 by the universal always-on /self-checkin page and had zero real traffic
    // (PROD: 0 event-self-checkin edge invocations over a 4-range-night peak; 0 attendee
    // rows from its channel). It stays as a PUBLIC redirect rather than a 404 because the
    // URL can still live on a printed QR code or a bookmark we do not have in the database,
    // so a visitor must land somewhere useful. The eventId is NOT carried through:
    // /self-checkin has no event-preselect parameter today (it only reads ?code=), and this
    // change invents none — the visitor picks their event from today's list there.
    path: "/event-self-checkin/:eventId",
    element: <Navigate to="/self-checkin" replace />,
  },
  {
    // PUBLIC universal, always-on self-check-in page (/self-checkin). Standalone, outside
    // DashboardLayout/RequireAuth. Scan a ticket/voucher (auto-resolves the event) or check in
    // as a walk-in (pick today's event → 3-factor member proof or guest). Phase 1.3c.
    path: "/self-checkin",
    element: <UniversalSelfCheckin />,
  },
  {
    // PUBLIC multi-behavior token redemption page (/t/:code). Standalone, outside
    // DashboardLayout/RequireAuth — reached by a holder scanning a token QR. Resolves the
    // token's available actions and redeems via the public token-public edge fn (Phase 1.3b).
    path: "/t/:code",
    element: <TokenRedeem />,
  },
  {
    // Public digital "forward to a friend" capture page — reached by a friend with no auth
    // session via the referral link in a member's voucher/reward email. Standalone, outside
    // DashboardLayout/RequireAuth (like /register/:id and /redeem).
    path: "/refer/:token",
    element: <ForwardToFriend />,
  },
  {
    // Tokenless verbal path: a friend who was given a member's number (no link) enters it here.
    path: "/refer",
    element: <ForwardToFriend />,
  },
  {
    path: "/qrcode",
    element: <QRCodeChecker />,
  },
  {
    path: "/lp/:slug",
    element: <LandingPagePublic />,
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <DashboardLayout />
      </RequireAuth>
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <Dashboard />
          </RequireRole>
        ),
      },
      {
        // Ad-hoc admin reward. Money: the route gate is UX only — issue_adhoc_reward_tx re-checks
        // the caller's role and is_active server-side and is EXECUTE-granted to service_role alone.
        path: "rewards/issue",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <AdhocReward />
          </RequireRole>
        ),
      },
      {
        // Partner-facing redemption analytics. Data is server-scoped by get_partner_scope
        // (auth.uid()), but the route is also role-gated (audit: previously any authenticated
        // user could reach it) to match /redemptions.
        path: "partner-dashboard",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "finance",
              "partner",
              "support_admin",
              "support_agent",
            ]}
          >
            <PartnerDashboard />
          </RequireRole>
        ),
      },
      {
        path: "organizations",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
              // Read-only viewer (owner 2026-11-03): view only; the database refuses its writes (migs 1620-1622).
              "readonly",
            ]}
          >
            <Organizations />
          </RequireRole>
        ),
      },
      {
        path: "organizations/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
              // Read-only viewer (owner 2026-11-03): view only; the database refuses its writes (migs 1620-1622).
              "readonly",
            ]}
          >
            <OrganizationDetail />
          </RequireRole>
        ),
      },
      {
        path: "organizations/:id/edit",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
            ]}
          >
            <OrganizationEdit />
          </RequireRole>
        ),
      },
      {
        path: "organizations/:id/locations/new",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
            ]}
          >
            <LocationAdd />
          </RequireRole>
        ),
      },
      {
        path: "organizations/:id/locations/:locationId/edit",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
            ]}
          >
            <LocationEdit />
          </RequireRole>
        ),
      },
      {
        // Five9 screen-pop landing target. Five9 is configured with
        //   https://<crm>/screen-pop?ani={{ANI}}
        // and opens it in the agent's browser on an inbound call. Same role set as the
        // contacts list: popping a record is a read of that record, so it cannot be broader.
        // RequireAuth preserves this destination through a login redirect, so a pop into a
        // lapsed session completes after sign-in rather than being lost.
        path: "screen-pop",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <ScreenPop />
          </RequireRole>
        ),
      },
      {
        path: "contacts",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
              // Read-only viewer (owner 2026-11-03): view only; the database refuses its writes (migs 1620-1622).
              "readonly",
            ]}
          >
            <Contacts />
          </RequireRole>
        ),
      },
      {
        path: "contacts/attributes",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <ContactAttributesAdmin />
          </RequireRole>
        ),
      },
      {
        path: "admin/ad-sync",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "it"]}>
            <AdSyncRuns />
          </RequireRole>
        ),
      },
      {
        // NeverBounce Former-Member background validation campaign (admin-only). The page itself is
        // dark-guarded on featureFlags.neverbounceCampaignBackfill and renders a disabled notice + makes
        // no server calls until the feature is activated. Route is admin/superadmin only.
        path: "admin/email-validation-campaign",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <NeverBounceCampaignAdmin />
          </RequireRole>
        ),
      },
      {
        // SendGrid ASM subscription-group catalog. Read for all authenticated
        // users; the page gates write controls to super_admin/admin/it (RLS enforces).
        path: "email-subscription-groups",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "superadmin",
              "admin",
              "it",
              "manager",
              "staff",
            ]}
          >
            <EmailSubscriptionGroups />
          </RequireRole>
        ),
      },
      {
        path: "admin/ad-sync/runs/:runId",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "it"]}>
            <AdSyncRunDetail />
          </RequireRole>
        ),
      },
      {
        // Phase 1.1 Ship 3 Layer 4 — repair tool for trigger-minted
        // generic CTAs whose QR generation failed. Admin/it gated.
        path: "admin/repair-generic-ctas",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "it"]}>
            <RepairGenericCTAs />
          </RequireRole>
        ),
      },
      {
        // SMS retention status (owner 2026-10-02): admins too, because the escalation owner is an
        // admin and the retention watchdog's alert links here. sms_retention_health() enforces it.
        path: "admin/sms-retention",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <SmsRetention />
          </RequireRole>
        ),
      },
      {
        // Zuora object-aware pipeline monitoring — reconciliation queue health,
        // stale backlog, and object-stream ingest failures. Admin gated.
        path: "admin/zuora-reconciliation",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <ZuoraReconciliationQueue />
          </RequireRole>
        ),
      },
      {
        // Held Zuora welcomes (2026-10-03-006): reviewed manual release / dismiss. The RPCs enforce active
        // admin/superadmin and every suppression server-side; this guard is only navigation.
        path: "admin/zuora-held-welcomes",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <ZuoraHeldWelcomes />
          </RequireRole>
        ),
      },
      {
        // Attorney question routing slice 2 — the agent surface for raising a question.
        // SHIPS DARK: a submitted question stops at "Waiting to send"; nothing is routed or
        // emailed yet. Support floor + management; create_attorney_question enforces the same
        // set server-side.
        path: "attorney-questions",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "support_admin",
              "support_agent",
            ]}
          >
            <AttorneyQuestions />
          </RequireRole>
        ),
      },
      {
        // Finance expenses (migrations 970-972). DARK in production by featureFlags.financeExpenses; the server
        // switch finance_settings.module_enabled and finance_my_access() are the real gate (FinanceLayout sends
        // anyone without access home), and every finance RPC / RLS policy refuses regardless of this guard.
        path: "finance",
        element: isFeatureEnabled("financeExpenses") ? (
          <RequireRole allowedRoles={FINANCE_ROLES}>
            <FinanceLayout />
          </RequireRole>
        ) : (
          <Navigate to="/" replace />
        ),
        children: [
          { index: true, element: <Navigate to="/finance/expenses" replace /> },
          { path: "expenses", element: <FinanceExpenses /> },
          { path: "approvals", element: <FinanceApprovals /> },
          { path: "ap", element: <FinanceAP /> },
          { path: "settings", element: <FinanceSettings /> },
        ],
      },
      {
        // Attorney question routing slice 1 — the state -> attorney coverage table.
        // SHIPS DARK: nothing routes or emails off it yet. Managers may READ coverage
        // (leadership oversight per BRD 4.9); only admin/superadmin may change it, and the
        // upsert/deactivate RPCs enforce that server-side regardless of this guard.
        path: "admin/attorney-coverage",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <AttorneyCoverage />
          </RequireRole>
        ),
      },
      {
        // Batch #5 — promotion auto-assign rules (state -> offer source of truth
        // the engine reads). Engine ships dormant (per-director toggle). Admin gated.
        path: "admin/promotion-rules",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <PromotionRules />
          </RequireRole>
        ),
      },
      {
        // Event Tickets — assign a real offer/value to tickets issued with a $0
        // placeholder reward. Leadership gated (matches assign_ticket_reward RPC
        // guard: superadmin/admin/manager). Dormant until placeholder rewards exist.
        path: "admin/ticket-rewards",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <TicketRewards />
          </RequireRole>
        ),
      },
      {
        // Workstream 4 Phase 1 — MSR portal schedule view. Admin /
        // super_admin can load the route for preview, but data RPCs
        // scope by auth.uid() so they only see their own assignments
        // (no impersonation).
        path: "msr",
        element: (
          <RequireRole allowedRoles={["msr", "super_admin", "admin"]}>
            <MsrSchedule />
          </RequireRole>
        ),
      },
      {
        path: "msr/events/:id",
        element: (
          <RequireRole allowedRoles={["msr", "super_admin", "admin"]}>
            <MsrEventDetail />
          </RequireRole>
        ),
      },
      {
        // MSR Portal — member lookup by email / member id. The msr_member_lookup RPC
        // fails closed on role and returns an allowlisted status+products snapshot.
        path: "msr/member-lookup",
        element: (
          // MSR plan slice 1 (MSR-05, owner decision 10): hidden for MSRs, and the RPC refuses role msr.
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <MemberLookup />
          </RequireRole>
        ),
      },
      {
        // MSR Availability Calendar — rep manages their own availability;
        // write access scoped server-side by sales_rep_availability RLS.
        path: "msr/availability",
        element: (
          <RequireRole allowedRoles={["msr", "super_admin", "admin"]}>
            <MsrAvailability />
          </RequireRole>
        ),
      },
      {
        // MSR Portal Phase 2 — per-rep Sales History (read-only). Data scoped
        // server-side to the caller's sales positions by get_msr_sales_history.
        path: "msr/sales-history",
        element: (
          <RequireRole allowedRoles={["msr", "super_admin", "admin"]}>
            <MsrSalesHistory />
          </RequireRole>
        ),
      },
      {
        // Business lead 2026-10-27: "Record Sale" became "Verify a Sale" (email only; credit when it is a new membership).
        path: "msr/verify-sale",
        element: (
          <RequireRole allowedRoles={["msr", "super_admin", "admin"]}>
            <MsrVerifySale />
          </RequireRole>
        ),
      },
      {
        path: "msr/record-sale",
        element: <Navigate to="/msr/verify-sale" replace />,
      },
      {
        // Managers: the team's availability (one calendar, or one rep), verified sales and typed-in reinstatements /
        // upgrades. The RPCs refuse anyone but superadmin/admin/manager/staff; removing a credit is superadmin/admin/manager.
        path: "msr-team",
        element: (
          // mig 1780: a scheduling delegate sees the Availability tab for the team they schedule for
          <RequireRoleOrSchedulingDelegate
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <MsrTeam />
          </RequireRoleOrSchedulingDelegate>
        ),
      },
      {
        path: "contacts/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
              // Not the read-only viewer role: owner 2026-11-04 -- it searches contacts but never opens a contact.
            ]}
          >
            <ContactDetail />
          </RequireRole>
        ),
      },
      {
        path: "segments",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "staff", "manager"]}
          >
            <Segments />
          </RequireRole>
        ),
      },
      {
        path: "suppression-list",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <SuppressionList />
          </RequireRole>
        ),
      },
      {
        path: "deduplication",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <Deduplication />
          </RequireRole>
        ),
      },
      {
        path: "tasks",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "staff", "manager"]}
          >
            <Tasks />
          </RequireRole>
        ),
      },
      {
        path: "support-tasks",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "support_admin",
              "support_agent",
            ]}
          >
            <SupportTasks />
          </RequireRole>
        ),
      },
      {
        // Pending Invitations admin surface — operators cancel
        // accidentally-sent or stale invites so the email can be
        // re-invited cleanly. Agent management lives in the modal
        // on Support Tasks (single source of truth); no separate
        // /admin/agents route.
        path: "admin/invitations",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "support_admin"]}>
            <PendingInvitations />
          </RequireRole>
        ),
      },
      {
        path: "event-performance",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <EventPerformance />
          </RequireRole>
        ),
      },
      {
        path: "events",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <EventsIndex />
          </RequireRole>
        ),
      },
      {
        // Calendar view of the same list. Declared before "events/:id" so the
        // literal segment always wins the match — the router has been bitten by
        // a literal-vs-:id ordering bug before (see /campaigns/create).
        // Role gate deliberately mirrors "events" exactly: this shows the same
        // rows, so it must not be visible to anyone the list is not.
        path: "events/calendar",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <EventsCalendar />
          </RequireRole>
        ),
      },
      {
        path: "events/create",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <EventCreate />
          </RequireRole>
        ),
      },
      {
        // Upcoming events without a Lead / Event Rep (mig 1630). Literal segment, declared before "events/:id".
        path: "events/missing-lead",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <EventsMissingLead />
          </RequireRole>
        ),
      },
      {
        // Events CSV import (gun shows + range nights). Literal segment, so it is declared before "events/:id".
        // Same roles as events/create; the database enforces the same set (migration 980).
        path: "events/import",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "staff", "manager"]}
          >
            <EventImport />
          </RequireRole>
        ),
      },
      {
        path: "events/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "regional_manager",
              "territory_manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <EventDetail />
          </RequireRole>
        ),
      },
      {
        path: "events/:id/edit",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "support_admin",
            ]}
          >
            <EventEdit />
          </RequireRole>
        ),
      },
      {
        path: "interactions",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <Interactions />
          </RequireRole>
        ),
      },
      {
        path: "calendar-requests",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "staff",
              "manager",
              "support_admin",
              "support_agent",
            ]}
          >
            <CalendarRequests />
          </RequireRole>
        ),
      },
      {
        path: "campaigns",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <Campaigns />
          </RequireRole>
        ),
      },
      {
        path: "campaigns/create",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <CampaignCreate />
          </RequireRole>
        ),
      },
      // Lookups routes — concrete paths come BEFORE the parameterized
      // campaigns/:id below, otherwise React Router matches /:id first.
      {
        path: "campaigns/lookups",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <CampaignLookups />
          </RequireRole>
        ),
      },
      {
        path: "campaigns/lookups/new",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <CampaignLookupEditor mode="create" />
          </RequireRole>
        ),
      },
      {
        path: "campaigns/lookups/:id",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <CampaignLookupEditor mode="edit" />
          </RequireRole>
        ),
      },
      {
        path: "campaigns/:campaignId/flights/:flightId",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <FlightDetail />
          </RequireRole>
        ),
      },
      {
        path: "campaigns/:id",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <CampaignDetail />
          </RequireRole>
        ),
      },
      {
        path: "offers",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <Offers />
          </RequireRole>
        ),
      },
      {
        path: "sequences",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <Sequences />
          </RequireRole>
        ),
      },
      {
        path: "sequences/create",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <SequenceCreate />
          </RequireRole>
        ),
      },
      {
        // Triage route must come BEFORE /sequences/:id so React Router
        // doesn't treat "triage" as a sequence id.
        path: "sequences/triage",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <SequenceErrorTriage />
          </RequireRole>
        ),
      },
      {
        path: "sequences/:id",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <SequenceDetail />
          </RequireRole>
        ),
      },
      {
        path: "sequences/:id/edit",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <SequenceCreate />
          </RequireRole>
        ),
      },
      {
        path: "sales-team",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <SalesTeam />
          </RequireRole>
        ),
      },
      // R5 (2026-05-17) — Reports suite.
      {
        path: "reports",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <Reports />
          </RequireRole>
        ),
      },
      {
        // Owner decision 2026-10-01: SMS messages held past 24 months by open work. Admins only (route + server).
        path: "reports/sms-retention-held",
        element: isFeatureEnabled("operatorSmsConversations") ? (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <SmsRetentionHeldReport />
          </RequireRole>
        ) : (
          <Navigate to="/reports" replace />
        ),
      },
      {
        path: "reports/events-by-organization",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <EventsByOrganizationReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/event-attendance",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <EventAttendanceReport />
          </RequireRole>
        ),
      },
      {
        // Conversion Engine Stage 4 — Report A. RPC enforces role server-side too.
        path: "reports/conversion-summary",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <ConversionSummaryReport />
          </RequireRole>
        ),
      },
      {
        // Conversion Engine Stage 4 — Report B (promoter inputs; RPC role-checked).
        path: "reports/promoter-fee-reconciliation",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <PromoterFeeReconciliationReport />
          </RequireRole>
        ),
      },
      {
        // Conversion Engine Stage 4 — Report C (MSR book of business; RPC role-checked).
        path: "reports/msr-book-of-business",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <MsrBookOfBusinessReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/verified-members",
        // PII report — tighter gate than other reports per R5-D5.
        // RPC also enforces role server-side (defense in depth).
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <VerifiedMembersReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/sales-activity-by-type",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <SalesActivityByTypeReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/sales-by-rep",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <SalesByRepReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/phone-sales",
        // R7a: PII report — admin/manager only. RPC also gates server-side.
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <PhoneSalesReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/counter-sales",
        // R7a: PII report — admin/manager only. RPC also gates server-side.
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <CounterSalesReport />
          </RequireRole>
        ),
      },
      {
        path: "reports/counter-sales/aggregate",
        // R7a: aggregate (no PII) — staff allowed too for commission visibility.
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <CounterSalesAggregateReport />
          </RequireRole>
        ),
      },
      {
        path: "sales-team/create",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <SalesPositionDetail />
          </RequireRole>
        ),
      },
      {
        path: "sales-team/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <SalesPositionDetail />
          </RequireRole>
        ),
      },
      {
        // R7a: phone sales list (also acts as the Sales Activities landing).
        path: "sales-activities/phone-sales",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <PhoneSalesList />
          </RequireRole>
        ),
      },
      {
        // R7a: phone sale create form. Standalone path; the Contact
        // Detail QuickActionsSection links here with ?contact_id=… to
        // prefill and lock the contact.
        path: "sales-activities/phone-sales/new",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <PhoneSaleNew />
          </RequireRole>
        ),
      },
      {
        // R7a: phone sale detail. Read-only fields + cancel button
        // (creator + admin/manager only — enforced by cancel_phone_sale).
        path: "sales-activities/phone-sales/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <PhoneSaleDetail />
          </RequireRole>
        ),
      },
      {
        // SA-6: MSR commission estimator (standalone for now; folds into the
        // MSR portal / SA-5 later). MSR-facing, so msr is allowed alongside
        // staff/manager/admin (audit fix — msr is a real role).
        path: "commission-estimator",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
              "msr",
            ]}
          >
            <CommissionEstimator />
          </RequireRole>
        ),
      },
      {
        path: "account-reviews",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "manager", "staff"]}
          >
            <AccountReviews />
          </RequireRole>
        ),
      },
      {
        path: "account-reviews/create",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "manager", "staff"]}
          >
            <AccountReviewDetail />
          </RequireRole>
        ),
      },
      {
        path: "account-reviews/:id",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "manager", "staff"]}
          >
            <AccountReviewDetail />
          </RequireRole>
        ),
      },
      {
        path: "sales-calendar",
        element: (
          // mig 1780: also a scheduling delegate (e.g. an MSR who schedules a Territory Manager's team)
          <RequireRoleOrSchedulingDelegate
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <SalesCalendar />
          </RequireRoleOrSchedulingDelegate>
        ),
      },
      {
        // mig 1780: Confirm Schedule (the event's Sales Reps list) on its own page, for scheduling delegates who do not
        // get the full event page (an MSR). Staff roles may open it too.
        path: "schedule/events/:id",
        element: (
          <RequireRoleOrSchedulingDelegate
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
            ]}
          >
            <ScheduleEventConfirm />
          </RequireRoleOrSchedulingDelegate>
        ),
      },
      {
        path: "partners",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <Partners />
          </RequireRole>
        ),
      },
      {
        path: "partners/new",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <PartnerDetail />
          </RequireRole>
        ),
      },
      {
        path: "partners/:id",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
            ]}
          >
            <PartnerDetail />
          </RequireRole>
        ),
      },
      {
        path: "content",
        element: (
          <RequireRole
            allowedRoles={["super_admin", "admin", "manager", "staff"]}
          >
            <Content />
          </RequireRole>
        ),
      },
      {
        path: "landing-pages",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <LandingPages />
          </RequireRole>
        ),
      },
      {
        path: "landing-pages/create",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <LandingPageDetail />
          </RequireRole>
        ),
      },
      {
        path: "landing-pages/:id",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <LandingPageDetail />
          </RequireRole>
        ),
      },
      // /qr-codes ("Voucher Codes") retired 2026-10-16 (owner): it could never generate a code
      // (qr_codes.code / qr_url NOT NULL, never produced) and qr_codes holds 0 rows in QA and PROD.
      // The qr_codes table is left untouched. Voucher links + QR codes: organization Calls to Action.
      {
        path: "redemptions",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "finance",
              "partner",
              "support_admin",
              "support_agent",
            ]}
          >
            <Redemptions />
          </RequireRole>
        ),
      },
      {
        path: "voucher-templates",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <VoucherTemplates />
          </RequireRole>
        ),
      },
      {
        path: "partner-offers",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <PartnerOffers />
          </RequireRole>
        ),
      },
      {
        path: "partner-offers/:id",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin", "manager"]}>
            <PartnerOfferDetail />
          </RequireRole>
        ),
      },
      {
        path: "form-handlers",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <FormHandlers />
          </RequireRole>
        ),
      },
      {
        path: "form-handlers/new",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <FormHandlerEdit />
          </RequireRole>
        ),
      },
      {
        path: "form-handlers/:id/edit",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <FormHandlerEdit />
          </RequireRole>
        ),
      },
      {
        path: "form-handlers/:id/submissions",
        element: (
          <RequireRole allowedRoles={["super_admin", "admin"]}>
            <FormHandlerSubmissions />
          </RequireRole>
        ),
      },
      {
        // In-app release notes. The role list mirrors can_read_release_notes() in the database
        // (migration 2026-09-24-009) EXACTLY: every active internal role, and no external one.
        // If the two drift, a role either gets a link to a page that renders nothing, or is
        // refused a page whose data it can already read.
        //
        // ⚠️ RequireRole normalizes underscores away, so "super_admin" here matches the
        // `superadmin` enum value stored on the profile; "support_admin" matches
        // `support_admin`. Both spellings are the ones used by every other route in this file.
        path: "whats-new",
        element: (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "manager",
              "regional_manager",
              "territory_manager",
              "staff",
              "support_admin",
              "support_agent",
              "it",
              "finance",
              "readonly",
              // "msr" removed 2026-10-19 (MSR QA notes, item 10): no What's New for field reps.
            ]}
          >
            <WhatsNew />
          </RequireRole>
        ),
      },
      {
        // SMS review queue. Gated to the SENDING roles rather than the wider read set: resolving an
        // item is a decision about whether a member was contacted, and about whether we keep not
        // texting them. Reading SMS history is a broader right than deciding these.
        path: "sms-review",
        // ⭐ Feature-gated as well as role-gated. The route is not in the nav, but a bare URL would
        // still reach it — and in production the SMS tables do not exist at all, so the page would
        // render an error rather than a queue. Dark has to mean the route too, not just the button.
        element: isFeatureEnabled("operatorSms") ? (
          <RequireRole
            allowedRoles={[
              "super_admin",
              "admin",
              "support_admin",
              "support_agent",
            ]}
          >
            <SmsReviewQueue />
          </RequireRole>
        ) : (
          <Navigate to="/dashboard" replace />
        ),
      },
      {
        // Two-way SMS inbox — READ side only (plan §9.4). Three gates: the frontend flag (does the UI
        // ask at all), the conversation roles, and the server probe sms_conversations_available()
        // inside SmsConversationsGate (allowlist during internal proof). A bare URL never renders the
        // surface to someone the server would refuse.
        path: "messages",
        element: isFeatureEnabled("operatorSmsConversations") ? (
          <RequireRole allowedRoles={SMS_CONVERSATION_ROLES}>
            <SmsConversationsGate>
              <SmsInbox />
            </SmsConversationsGate>
          </RequireRole>
        ) : (
          <Navigate to="/dashboard" replace />
        ),
      },
      {
        path: "messages/:id",
        element: isFeatureEnabled("operatorSmsConversations") ? (
          <RequireRole allowedRoles={SMS_CONVERSATION_ROLES}>
            <SmsConversationsGate>
              <SmsThread />
            </SmsConversationsGate>
          </RequireRole>
        ) : (
          <Navigate to="/dashboard" replace />
        ),
      },
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "error-logs",
        element: (
          <RequireRole allowedRoles={["super_admin"]}>
            <ErrorLogs />
          </RequireRole>
        ),
      },
    ],
  },
];

// Locator hosts (qa-locator / locator.uslawshield.com) know ONLY the public locator page: nginx serves nothing else
// there, and this router makes sure a client-side navigation cannot render a CRM screen on that origin either.
const locatorHostRoutes: RouteObject[] = [
  { path: LOCATOR_PATH, element: <VoucherLocator /> },
  { path: "*", element: <Navigate to={LOCATOR_PATH} replace /> },
];

export const router = createBrowserRouter(
  typeof window !== "undefined" && isLocatorHost(window.location.hostname)
    ? locatorHostRoutes
    : crmRoutes,
);
